<template>
  <div id="create_new_chat" :class="isEmbeddedMode ? 'h-full' : ''">
    <div id="create_new_chat_content" class="flex flex-col h-full">
      <div
        id="create_new_chat_header"
        class="flex flex-col gap-3 p-3 border_bottom"
      >
        <div
          class="flex flex-row items-center gap-2 cursor-pointer w-fit"
          @click="handleGoBack"
        >
          <ThemeImage
            class="size-4"
            :icon="imageNames.BACK_ARROW_DEFAULT_GREY"
            alt="back_button_icon"
          />

          <span class="text-sm font-semibold">New Chat</span>
        </div>

        <a-select
          v-model:value="selectedUserIds"
          mode="multiple"
          placeholder="Select users"
          class="w-full member-select"
          show-search
          allow-clear
          option-filter-prop="label"
          :options="userOptions"
          :maxTagCount="10"
          :maxTagPlaceholder="(omittedValues) => `+${omittedValues.length} more`"
          @change="handleSelectChange"
        />

      </div>

      <div
        id="create_new_chat_body"
        class="flex flex-col gap-2 p-2 overflow-y-scroll"
        :class="isEmbeddedMode ? 'flex-1 min-h-0' : ''"
        :style="isEmbeddedMode ? {} : { height: 'calc(100vh - (130px + var(--banner-height)))' }"
      >
        <div
          class="flex flex-row gap-2 cursor-pointer items-center px-2 py-[10px]"
          @click="handleNewGroupClick"
        >
          <ProfileIcon data="" :size="38" />
          <span class="text-sm primary_color">New Group</span>
        </div>

        <UserList source="create_new_chat" />
      </div>
    </div>

    <div id="create_new_chat_actions">
      <AddOrEditGroupModal
        source="create_new_chat"
        v-model:visible="showAddNewGroupModal"
        :selected-users="selectedUsers"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, watch, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { renderEntity } from "@/src/utils/utils.js";
import { createPlaceholderGroupDM } from "@/src/utils/chatUtils.js";
import { chatViews } from "@/src/constants/chatConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import UserList from "@/src/views/projects/chats/side_panel/create/UserList.vue";
import AddOrEditGroupModal from "@/src/components/projects_components/chats/modals/AddOrEditGroupModal.vue";

const emit = defineEmits(["toggle-view"]);
const store = useStore();
const isEmbeddedMode = inject("embeddedChatMode", false);
const openEmbeddedChat = inject("openEmbeddedChat", null);

const showAddNewGroupModal = ref(false);
const activePlaceholderId = ref(null);
const isUnmounting = ref(false);
const currentUserId = computed(() => store.getters["auth/userData"]?.id);

const allUsers = computed(() => {
  return (store.getters["users/users"] || []).filter((user) => user.id !== currentUserId.value);
});

const userOptions = computed(() => {
  return allUsers.value.map((user) => ({
    value: user.id,
    label: renderEntity(user),
  }));
});

const selectedUserIds = computed({
  get: () => store.getters["chats/newChatUserList"].map((u) => u.id),
  set: (ids) => {
    const users = ids
      .map((id) => allUsers.value.find((u) => u.id === id))
      .filter(Boolean);
    store.dispatch("chats/setChatUsers", users);
  },
});

const selectedUsers = computed(() => {
  return store.getters["chats/newChatUserList"] || [];
});

const handleSelectChange = (ids) => {
  try {
    const users = ids
      .map((id) => allUsers.value?.find((u) => u.id === id))
      .filter(Boolean);
    store.dispatch("chats/setChatUsers", users);
  } catch (error) {
    console.error("An error occurred while handling select change: ", error);
  }
};

const handleNewGroupClick = () => {
  try {
    showAddNewGroupModal.value = true;
  } catch (error) {
    console.error("An error occurred while handling new group click: ", error);
  }
};

const handleGoBack = () => {
  try {
    emit("toggle-view", chatViews.BROWSER);
  } catch (error) {
    console.error("An error occurred while going back: ", error);
  }
};


const handleGroupCreated = async (newGroup) => {
  try {
    if (newGroup) {
      await store.dispatch("chats/addGroupToList", newGroup);
      if (isEmbeddedMode && openEmbeddedChat) {
        openEmbeddedChat(newGroup);
      } else {
        await store.dispatch("chats/setActiveChat", newGroup);
      }
    }
    emit("toggle-view", chatViews.BROWSER);
  } catch (error) {
    console.error("An error occurred while handling group created: ", error);
  }
};

watch(
  () => selectedUsers.value.length,
  (count) => {
    if (!isEmbeddedMode || !openEmbeddedChat || isUnmounting.value) return;

    if (count >= 2) {
      // Close previous placeholder if users changed
      if (activePlaceholderId.value) {
        store.dispatch("embeddedChats/closeChat", activePlaceholderId.value);
      }
      const placeholder = createPlaceholderGroupDM([...selectedUsers.value], renderEntity);
      activePlaceholderId.value = placeholder.id;
      openEmbeddedChat(placeholder);
    } else if (activePlaceholderId.value) {
      // Deselected below 2 — close the placeholder
      store.dispatch("embeddedChats/closeChat", activePlaceholderId.value);
      activePlaceholderId.value = null;
    }
  },
);

onMounted(() => {
  store.dispatch("chats/clearNewChat");
  if (
    !store.getters["users/users"] ||
    store.getters["users/users"].length === 0
  ) {
    store.dispatch("users/fetchUsers");
  }
  eventBus.on("new-message-closed", handleGoBack);
  eventBus.on("group-created", handleGroupCreated);
});

onUnmounted(() => {
  isUnmounting.value = true;
  activePlaceholderId.value = null;
  store.dispatch("chats/clearNewChat");
  eventBus.off("new-message-closed", handleGoBack);
  eventBus.off("group-created", handleGroupCreated);
});
</script>

<style scoped>
.member-select :deep(.ant-select-selector) {
  max-height: 120px;
  overflow-y: auto !important;
}
</style>
